import { create } from 'zustand';
import api from '../lib/api';
import type {
  Region,
  Territory,
  NamedAccount,
  AssignmentRule,
  MapData,
  CoverageStats,
  OwnerResolution,
  RealignmentPreview,
  RegionCreate,
  RegionUpdate,
  TerritoryCreate,
  TerritoryUpdate,
  NamedAccountCreate,
  TerritoryFilters,
  NamedAccountFilters,
  StateDetail,
  AnalyzeResponse,
  AnalyzeSettings,
  ClusterDetail,
} from '../types';
import { DEFAULT_ANALYZE_SETTINGS } from '../types';

interface TerritoryState {
  // Regions
  regions: Region[];
  isLoadingRegions: boolean;
  regionsError: string | null;

  // Territories
  territories: Territory[];
  isLoadingTerritories: boolean;
  territoriesError: string | null;
  territoryFilters: TerritoryFilters;

  // Named Accounts
  namedAccounts: NamedAccount[];
  isLoadingNamedAccounts: boolean;
  namedAccountsError: string | null;
  namedAccountFilters: NamedAccountFilters;

  // Map
  mapData: MapData | null;
  isLoadingMap: boolean;
  mapError: string | null;

  // Coverage Stats
  coverageStats: CoverageStats | null;
  isLoadingStats: boolean;

  // Assignment Rules
  assignmentRules: AssignmentRule[];
  isLoadingRules: boolean;

  // Selection (for detail views)
  selectedRegion: Region | null;
  selectedTerritory: Territory | null;

  // Realignment
  realignmentPreview: RealignmentPreview | null;
  isPreviewLoading: boolean;

  // State Detail
  stateDetail: StateDetail | null;
  isLoadingStateDetail: boolean;
  stateDetailError: string | null;

  // Analysis
  analyzeData: AnalyzeResponse | null;
  analyzeSettings: AnalyzeSettings;
  isLoadingAnalysis: boolean;
  analyzeError: string | null;
  selectedClusterId: string | null;

  // Cluster Detail
  clusterDetail: ClusterDetail | null;
  isLoadingClusterDetail: boolean;
  clusterDetailError: string | null;

  // ==================== REGION ACTIONS ====================
  fetchRegions: () => Promise<void>;
  createRegion: (data: RegionCreate) => Promise<Region>;
  updateRegion: (id: string, data: RegionUpdate) => Promise<Region>;
  deleteRegion: (id: string) => Promise<void>;

  // ==================== TERRITORY ACTIONS ====================
  fetchTerritories: () => Promise<void>;
  createTerritory: (data: TerritoryCreate) => Promise<Territory>;
  updateTerritory: (id: string, data: TerritoryUpdate) => Promise<Territory>;
  deleteTerritory: (id: string) => Promise<void>;
  replaceBoundaries: (id: string, data: { states?: string[]; zipCodes?: string[] }) => Promise<Territory>;

  // ==================== NAMED ACCOUNT ACTIONS ====================
  fetchNamedAccounts: () => Promise<void>;
  createNamedAccount: (data: NamedAccountCreate) => Promise<NamedAccount>;
  updateNamedAccount: (id: string, data: { assignedUserId: string }) => Promise<NamedAccount>;
  deleteNamedAccount: (id: string) => Promise<void>;

  // ==================== ASSIGNMENT RULE ACTIONS ====================
  fetchAssignmentRules: () => Promise<void>;
  updateAssignmentRule: (id: string, data: { isActive?: boolean; priority?: number }) => Promise<AssignmentRule>;

  // ==================== MAP AND STATS ACTIONS ====================
  fetchMapData: () => Promise<void>;
  fetchCoverageStats: () => Promise<void>;

  // ==================== OWNER RESOLUTION ====================
  resolveOwner: (orgId: string) => Promise<OwnerResolution>;

  // ==================== REALIGNMENT ACTIONS ====================
  previewRealignment: (changes: { territoryId: string; states?: string[]; zipCodes?: string[] }[]) => Promise<RealignmentPreview>;
  commitRealignment: (token: string) => Promise<void>;

  // ==================== STATE DETAIL ACTIONS ====================
  fetchStateDetail: (stateCode: string) => Promise<void>;
  clearStateDetail: () => void;

  // ==================== ANALYSIS ACTIONS ====================
  fetchAnalysis: (settings?: AnalyzeSettings) => Promise<void>;
  setAnalyzeSettings: (settings: Partial<AnalyzeSettings>) => void;
  setSelectedCluster: (id: string | null) => void;

  // ==================== CLUSTER DETAIL ACTIONS ====================
  fetchClusterDetail: (lat: number, lng: number, radiusMiles: number) => Promise<void>;
  clearClusterDetail: () => void;

  // ==================== FILTER ACTIONS ====================
  setTerritoryFilters: (filters: Partial<TerritoryFilters>) => void;
  setNamedAccountFilters: (filters: Partial<NamedAccountFilters>) => void;

  // ==================== UTILITY ACTIONS ====================
  clearErrors: () => void;
}

export const useTerritoryStore = create<TerritoryState>((set, get) => ({
  // Initial state — Regions
  regions: [],
  isLoadingRegions: false,
  regionsError: null,

  // Initial state — Territories
  territories: [],
  isLoadingTerritories: false,
  territoriesError: null,
  territoryFilters: {},

  // Initial state — Named Accounts
  namedAccounts: [],
  isLoadingNamedAccounts: false,
  namedAccountsError: null,
  namedAccountFilters: {},

  // Initial state — Map
  mapData: null,
  isLoadingMap: false,
  mapError: null,

  // Initial state — Coverage Stats
  coverageStats: null,
  isLoadingStats: false,

  // Initial state — Assignment Rules
  assignmentRules: [],
  isLoadingRules: false,

  // Initial state — Selection
  selectedRegion: null,
  selectedTerritory: null,

  // Initial state — Realignment
  realignmentPreview: null,
  isPreviewLoading: false,

  // Initial state — State Detail
  stateDetail: null,
  isLoadingStateDetail: false,
  stateDetailError: null,

  // Initial state — Analysis
  analyzeData: null,
  analyzeSettings: (() => {
    try {
      const saved = localStorage.getItem('analyzeSettings');
      return saved ? { ...DEFAULT_ANALYZE_SETTINGS, ...JSON.parse(saved) } : DEFAULT_ANALYZE_SETTINGS;
    } catch {
      return DEFAULT_ANALYZE_SETTINGS;
    }
  })(),
  isLoadingAnalysis: false,
  analyzeError: null,
  selectedClusterId: null,

  // Initial state — Cluster Detail
  clusterDetail: null,
  isLoadingClusterDetail: false,
  clusterDetailError: null,

  // ==================== REGION ACTIONS ====================

  fetchRegions: async () => {
    try {
      set({ isLoadingRegions: true, regionsError: null });
      const res = await api.get('/api/regions');
      set({ regions: res.data.regions, isLoadingRegions: false });
    } catch (error: any) {
      console.error('Failed to fetch regions:', error);
      set({
        isLoadingRegions: false,
        regionsError: error.response?.data?.message || 'Failed to load regions',
      });
    }
  },

  createRegion: async (data: RegionCreate) => {
    const res = await api.post('/api/regions', data);
    const region: Region = res.data.region;
    set((state) => ({ regions: [...state.regions, region] }));
    return region;
  },

  updateRegion: async (id: string, data: RegionUpdate) => {
    const res = await api.patch(`/api/regions/${id}`, data);
    const updated: Region = res.data.region;
    set((state) => ({
      regions: state.regions.map((r) => (r.id === id ? updated : r)),
      selectedRegion: state.selectedRegion?.id === id ? updated : state.selectedRegion,
    }));
    return updated;
  },

  deleteRegion: async (id: string) => {
    await api.delete(`/api/regions/${id}`);
    set((state) => ({
      regions: state.regions.filter((r) => r.id !== id),
      selectedRegion: state.selectedRegion?.id === id ? null : state.selectedRegion,
    }));
  },

  // ==================== TERRITORY ACTIONS ====================

  fetchTerritories: async () => {
    const { territoryFilters } = get();
    try {
      set({ isLoadingTerritories: true, territoriesError: null });

      const params = new URLSearchParams();
      if (territoryFilters.regionId) params.append('regionId', territoryFilters.regionId);
      if (territoryFilters.assignedUserId) params.append('assignedUserId', territoryFilters.assignedUserId);
      if (territoryFilters.unassigned !== undefined) params.append('unassigned', territoryFilters.unassigned.toString());

      const query = params.toString() ? `?${params.toString()}` : '';
      const res = await api.get(`/api/territories${query}`);
      set({ territories: res.data.territories, isLoadingTerritories: false });
    } catch (error: any) {
      console.error('Failed to fetch territories:', error);
      set({
        isLoadingTerritories: false,
        territoriesError: error.response?.data?.message || 'Failed to load territories',
      });
    }
  },

  createTerritory: async (data: TerritoryCreate) => {
    const res = await api.post('/api/territories', data);
    const territory: Territory = res.data.territory;
    set((state) => ({ territories: [...state.territories, territory] }));
    return territory;
  },

  updateTerritory: async (id: string, data: TerritoryUpdate) => {
    const res = await api.patch(`/api/territories/${id}`, data);
    const updated: Territory = res.data.territory;
    set((state) => ({
      territories: state.territories.map((t) => (t.id === id ? updated : t)),
      selectedTerritory: state.selectedTerritory?.id === id ? updated : state.selectedTerritory,
    }));
    return updated;
  },

  deleteTerritory: async (id: string) => {
    await api.delete(`/api/territories/${id}`);
    set((state) => ({
      territories: state.territories.filter((t) => t.id !== id),
      selectedTerritory: state.selectedTerritory?.id === id ? null : state.selectedTerritory,
    }));
  },

  replaceBoundaries: async (id: string, data: { states?: string[]; zipCodes?: string[] }) => {
    const res = await api.put(`/api/territories/${id}/boundaries`, data);
    const updated: Territory = res.data.territory;
    set((state) => ({
      territories: state.territories.map((t) => (t.id === id ? updated : t)),
      selectedTerritory: state.selectedTerritory?.id === id ? updated : state.selectedTerritory,
    }));
    return updated;
  },

  // ==================== NAMED ACCOUNT ACTIONS ====================

  fetchNamedAccounts: async () => {
    const { namedAccountFilters } = get();
    try {
      set({ isLoadingNamedAccounts: true, namedAccountsError: null });

      const params = new URLSearchParams();
      if (namedAccountFilters.organizationId) params.append('organizationId', namedAccountFilters.organizationId);
      if (namedAccountFilters.assignedUserId) params.append('assignedUserId', namedAccountFilters.assignedUserId);
      if (namedAccountFilters.source) params.append('source', namedAccountFilters.source);

      const query = params.toString() ? `?${params.toString()}` : '';
      const res = await api.get(`/api/named-accounts${query}`);
      set({ namedAccounts: res.data.namedAccounts, isLoadingNamedAccounts: false });
    } catch (error: any) {
      console.error('Failed to fetch named accounts:', error);
      set({
        isLoadingNamedAccounts: false,
        namedAccountsError: error.response?.data?.message || 'Failed to load named accounts',
      });
    }
  },

  createNamedAccount: async (data: NamedAccountCreate) => {
    const res = await api.post('/api/named-accounts', data);
    const namedAccount: NamedAccount = res.data.namedAccount;
    set((state) => ({ namedAccounts: [...state.namedAccounts, namedAccount] }));
    return namedAccount;
  },

  updateNamedAccount: async (id: string, data: { assignedUserId: string }) => {
    const res = await api.patch(`/api/named-accounts/${id}`, data);
    const updated: NamedAccount = res.data.namedAccount;
    set((state) => ({
      namedAccounts: state.namedAccounts.map((na) => (na.id === id ? updated : na)),
    }));
    return updated;
  },

  deleteNamedAccount: async (id: string) => {
    await api.delete(`/api/named-accounts/${id}`);
    set((state) => ({
      namedAccounts: state.namedAccounts.filter((na) => na.id !== id),
    }));
  },

  // ==================== ASSIGNMENT RULE ACTIONS ====================

  fetchAssignmentRules: async () => {
    try {
      set({ isLoadingRules: true });
      const res = await api.get('/api/assignment-rules');
      set({ assignmentRules: res.data.assignmentRules, isLoadingRules: false });
    } catch (error: any) {
      console.error('Failed to fetch assignment rules:', error);
      set({ isLoadingRules: false });
    }
  },

  updateAssignmentRule: async (id: string, data: { isActive?: boolean; priority?: number }) => {
    const res = await api.patch(`/api/assignment-rules/${id}`, data);
    const updated: AssignmentRule = res.data.assignmentRule;
    set((state) => ({
      assignmentRules: state.assignmentRules.map((r) => (r.id === id ? updated : r)),
    }));
    return updated;
  },

  // ==================== MAP AND STATS ACTIONS ====================

  fetchMapData: async () => {
    try {
      set({ isLoadingMap: true, mapError: null });
      const res = await api.get('/api/territories/map-data');
      set({ mapData: res.data, isLoadingMap: false });
    } catch (error: any) {
      console.error('Failed to fetch map data:', error);
      set({
        isLoadingMap: false,
        mapError: error.response?.data?.message || 'Failed to load map data',
      });
    }
  },

  fetchCoverageStats: async () => {
    try {
      set({ isLoadingStats: true });
      const res = await api.get('/api/territories/coverage-stats');
      set({ coverageStats: res.data.stats, isLoadingStats: false });
    } catch (error: any) {
      console.error('Failed to fetch coverage stats:', error);
      set({ isLoadingStats: false });
    }
  },

  // ==================== OWNER RESOLUTION ====================

  resolveOwner: async (orgId: string) => {
    const res = await api.get(`/api/organizations/${orgId}/owner`);
    return res.data as OwnerResolution;
  },

  // ==================== REALIGNMENT ACTIONS ====================

  previewRealignment: async (changes) => {
    try {
      set({ isPreviewLoading: true });
      const res = await api.post('/api/territories/realignment-preview', { changes });
      const preview: RealignmentPreview = res.data;
      set({ realignmentPreview: preview, isPreviewLoading: false });
      return preview;
    } catch (error: any) {
      set({ isPreviewLoading: false });
      throw error;
    }
  },

  commitRealignment: async (token: string) => {
    await api.post('/api/territories/realignment-commit', { previewToken: token });
    set({ realignmentPreview: null });
    // Refresh territories and map data after commit
    get().fetchTerritories();
    get().fetchMapData();
  },

  // ==================== STATE DETAIL ACTIONS ====================

  fetchStateDetail: async (stateCode: string) => {
    try {
      set({ isLoadingStateDetail: true, stateDetailError: null });
      const res = await api.get(`/api/territories/state-detail/${stateCode}`);
      set({ stateDetail: res.data, isLoadingStateDetail: false });
    } catch (error: any) {
      console.error('Failed to fetch state detail:', error);
      set({
        isLoadingStateDetail: false,
        stateDetailError: error.response?.data?.message || 'Failed to load state detail',
      });
    }
  },

  clearStateDetail: () => {
    set({ stateDetail: null, stateDetailError: null });
  },

  // ==================== ANALYSIS ACTIONS ====================

  fetchAnalysis: async (settings?: AnalyzeSettings) => {
    try {
      const s = settings ?? get().analyzeSettings;
      set({ isLoadingAnalysis: true, analyzeError: null });
      const params = new URLSearchParams({
        radiusMiles: String(s.radiusMiles),
        minSchools: String(s.minSchools),
        maxBlastPct: String(s.maxBlastPct),
        weightMode: String(s.weightMode),
        includeCompetitor: String(s.includeCompetitor),
      });
      const res = await api.get(`/api/territories/analyze?${params}`);
      set({ analyzeData: res.data, isLoadingAnalysis: false });
    } catch (error: any) {
      console.error('Failed to fetch analysis:', error);
      set({
        isLoadingAnalysis: false,
        analyzeError: error.response?.data?.message || 'Failed to load analysis',
      });
    }
  },

  setAnalyzeSettings: (settings: Partial<AnalyzeSettings>) => {
    set((state) => {
      const updated = { ...state.analyzeSettings, ...settings };
      try { localStorage.setItem('analyzeSettings', JSON.stringify(updated)); } catch {}
      return { analyzeSettings: updated };
    });
  },

  setSelectedCluster: (id: string | null) => {
    set({ selectedClusterId: id });
  },

  // ==================== CLUSTER DETAIL ACTIONS ====================

  fetchClusterDetail: async (lat: number, lng: number, radiusMiles: number) => {
    try {
      set({ isLoadingClusterDetail: true, clusterDetailError: null });
      const params = new URLSearchParams({
        lat: String(lat),
        lng: String(lng),
        radiusMiles: String(radiusMiles),
      });
      const res = await api.get(`/api/territories/cluster-detail?${params}`);
      set({ clusterDetail: res.data, isLoadingClusterDetail: false });
    } catch (error: any) {
      console.error('Failed to fetch cluster detail:', error);
      set({
        isLoadingClusterDetail: false,
        clusterDetailError: error.response?.data?.message || 'Failed to load cluster detail',
      });
    }
  },

  clearClusterDetail: () => {
    set({ clusterDetail: null, clusterDetailError: null });
  },

  // ==================== FILTER ACTIONS ====================

  setTerritoryFilters: (filters: Partial<TerritoryFilters>) => {
    set((state) => ({
      territoryFilters: { ...state.territoryFilters, ...filters },
    }));
    get().fetchTerritories();
  },

  setNamedAccountFilters: (filters: Partial<NamedAccountFilters>) => {
    set((state) => ({
      namedAccountFilters: { ...state.namedAccountFilters, ...filters },
    }));
    get().fetchNamedAccounts();
  },

  // ==================== UTILITY ACTIONS ====================

  clearErrors: () => {
    set({
      regionsError: null,
      territoriesError: null,
      namedAccountsError: null,
      mapError: null,
    });
  },
}));
